import { Avatar, Button, tokens } from '@gripp/shared-ui';
import { Modal, StyleSheet, Text, View } from 'react-native';

interface IncomingCallModalProps {
  callerName: string;
  /** Initials shown in the avatar, e.g. "PJ". */
  callerInitials: string;
  onAccept: () => void;
  onDecline: () => void;
  responding: boolean;
}

/**
 * Centered "X is calling you" prompt with Accept / Decline. Mounted by
 * CallProvider when an incoming-call signal arrives.
 */
export const IncomingCallModal = ({
  callerName,
  callerInitials,
  onAccept,
  onDecline,
  responding,
}: IncomingCallModalProps) => {
  return (
    <Modal visible transparent animationType="fade" onRequestClose={onDecline}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <Avatar
            size="extraLarge"
            type="initial"
            shape="rounded"
            text={callerInitials}
          />
          <View style={styles.textBlock}>
            <Text style={styles.name} numberOfLines={1}>
              {callerName}
            </Text>
            <Text style={styles.subtitle}>is calling you…</Text>
          </View>
          <View style={styles.actions}>
            <Button
              state="warning"
              size="large"
              onPress={onDecline}
              disabled={responding}
              accessibilityLabel="Decline call"
              style={styles.actionButton}
            >
              Decline
            </Button>
            <Button
              state="active"
              size="large"
              onPress={onAccept}
              loading={responding}
              accessibilityLabel="Accept call"
              style={styles.actionButton}
            >
              Accept
            </Button>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  card: {
    width: 320,
    alignItems: 'center',
    gap: tokens.spacing.lg,
    padding: tokens.spacing.xl,
    borderRadius: tokens.borderRadius.lg,
    backgroundColor: tokens.colors.brand.white,
    ...tokens.shadows.lg,
  },
  textBlock: {
    alignItems: 'center',
    gap: tokens.spacing.xs,
  },
  name: {
    ...tokens.typography.title.medium,
    color: tokens.colors.neutral[800],
  },
  subtitle: {
    ...tokens.typography.body.medium,
    color: tokens.colors.neutral[600],
  },
  actions: {
    flexDirection: 'row',
    gap: tokens.spacing.md,
    alignSelf: 'stretch',
  },
  actionButton: {
    flex: 1,
  },
});
